<template>
  <BaseBody>
    <BaseTopBar>
      <BaseTopBarContent className="left pr-0">
        {{ $t('sidebar.lotnumber') }} > {{ lotSingle.lotCode }}
      </BaseTopBarContent>
      <BaseTopBarContent className="right pl-0">
        <div
          v-if="!loading"
          class="action">
          <template v-if="device.isMobile">
            <BaseActionDropdown
              :icon="`fas fa-cog`"
              :text="$t('button.options')"
              :items="[
                {
                  icon: 'fas fa-copy',
                  iconClassName: '',
                  text: $t('button.clone'),
                  textClassName: '',
                  value: 'clone',
                },
                {
                  icon: 'fas fa-trash',
                  iconClassName: 'text-danger',
                  text: $t('button.delete'),
                  textClassName: 'text-danger',
                  value: 'delete',
                }
              ]"
              @onSelect="handleSelectOption"
            />
          </template>
          <template v-else>
            <BaseActionDropdown
              v-if="hasRoleDelete"
              :icon="`fas fa-cog`"
              :text="$t('button.options')"
              :items="[
                {
                  icon: 'fas fa-trash',
                  iconClassName: 'text-danger',
                  text: $t('button.delete'),
                  textClassName: 'text-danger',
                  value: 'delete',
                }
              ]"
              @onSelect="handleSelectOption"
            />
            <BaseAction
              :icon="`fas fa-copy`"
              :iconClassName="``"
              :text="$t('button.clone')"
              :textClassName="``"
              @onClick="openCloneModal"
            />
          </template>
          <template>
            <div
              v-if="loadingStatus"
              class="dropdown nodro item">
              <i class="fas fa-spinner spin"/>
            </div>
            <template v-else>
              <BaseActionDropdown
                v-if="hasRoleEdit"
                :className="statusColor"
                :text="statusText"
                :items="[
                  {
                    text: $t('apps.inventory.message.lotready'),
                    textClassName: 'text-success',
                    value: true,
                    disabled: isEmpty,
                  },
                  {
                    text: $t('apps.inventory.message.lotnotready'),
                    textClassName: 'text-warning',
                    value: false,
                    disabled: isEmpty,
                  }
                ]"
                @onSelect="handleSelectStatus"
              />
              <div
                v-if="!hasRoleEdit"
                class="dropdown item">
                <span
                  :class="[statusColor]">
                  {{ statusText }}
                </span>
              </div>
            </template>
          </template>
        </div>
      </BaseTopBarContent>
    </BaseTopBar>
    <BaseContent :loading="loading">
      <WrapperDoc>
        <template #form>
          <ImsLotNumberForm
            :state="lotSingle"
            :lotCode="lotSingle.lotCode"
            :isSubmitted="isSubmitted"
            :warehouses="warehouses"
            :products="products"
            :disabled="disabledForm"
            :form.sync="form"
            :showActionButtons="!disabledForm"
            @onSubmit="handleSubmit"
          />

          <baseConfirmDeleteModal
            :show="modal.show"
            :loading="modal.loading"
            @onClose="handleCloseModal"
            @onCancel="handleCancleModal"
            @onSubmit="handleSumitModal"
          />

          <BaseConfirmDiscardModal
            :show="confirmDiscardModal.show"
            @onCancel="handleDiscardCancleModal"
            @onConfirm="handleDiscardConfirmModal"
          />

          <BaseConfirmCloneModal
            :show="modalConfirmClone.show"
            :loading="modalConfirmClone.loading"
            @onClose="handleCloseCloneModal"
            @onCancel="handleCancleCloneModal"
            @onConfirm="handleConfirmCloneModal({
              payload: payloadClone,
              createApiFn: createLot,
              redirectTo: 'InventoryLotList',
              refCode: 'lot_code',
            })"
          />
        </template>
        <template #ref>
          <DocRef
            :refDoc="`lot`"
            :refId="lotSingle.lotId"
            :refCode="lotSingle.lotCode"
            :showLog="getInventorySetting.log_topic.is_active"
            :showAttachFile="getInventorySetting.attach_file.is_active"
            :logTopics="getInventorySetting.log_topic.options"
            :isSubmitted="isSubmitted"
          />
        </template>
      </WrapperDoc>
    </BaseContent>
  </BaseBody>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { toISODateString } from '@/helpers'
import sidebarModule from './../../sidebarModule'
import submenuModule from '../submenuModule'
import discardFormMixin from '@/mixins/discardForm'
import userPermissionMixin from '@/mixins/userPermission'
import confirmCloneMixin from '@/mixins/confirmClone'
import WrapperDoc from '@/components/combine/WrapperDoc.vue'
import DocRef from '@/components/combine/DocRef.vue'
import BaseConfirmCloneModal from '@/components/base/BaseConfirmCloneModal'

export default {
  components: {
    WrapperDoc,
    DocRef,
    BaseConfirmCloneModal,
  },
  mixins: [discardFormMixin, userPermissionMixin, confirmCloneMixin],
  data () {
    return {
      loading: true,
      loadingStatus: false,
      isSubmitted: false,
      query: {
        id: this.$route.query.id,
        code: this.$route.query.code,
      },
      modal: {
        show: false,
        loading: false,
      },
      form: null,
    }
  },

  computed: {
    ...mapState('lot', {
      lotSingle: 'lotSingle',
    }),
    ...mapGetters({
      sidebar: 'getSidebar',
      warehouses: 'getWarehouses',
      products: 'getProducts',
      getProductInfoBySkuId: 'getProductInfoBySkuId',
      getInventorySetting: 'getInventorySetting',
    }),
    isEmpty () {
      return this.lotSingle.unitBalance <= 0
    },
    statusColor () {
      if (this.isEmpty) {
        return 'text-grey'
      }
      return this.lotSingle.isReady
        ? 'text-success'
        : 'text-warning'
    },
    statusText () {
      if (this.isEmpty) {
        return this.$t('apps.inventory.message.lotempty')
      }
      return this.lotSingle.isReady
        ? this.$t('apps.inventory.message.lotready')
        : this.$t('apps.inventory.message.lotnotready')
    },
    disabledForm () {
      return !this.hasRoleEdit
    },
    payloadClone() {
      const productInfo = this.getProductInfoBySkuId(this.lotSingle.skuId)
      const productId = productInfo.product_id
      const stockId = productInfo
        .prices.find((price) => price.sku_id === this.lotSingle.skuId)
        .stocks.find((stock) => stock.warehouse_id === this.lotSingle.whId)
        .stock_id
      return {
        'stock_id': stockId,
        'sku_id': this.lotSingle.skuId,
        'product_id': productId,
        'wh_id': this.lotSingle.whId,
        'bin_location_id': this.lotSingle.binLocationId,
        'gr_id': null,
        'po_id': null,
        'date_in': this.lotSingle.dateIn,
        'date_product': this.lotSingle.dateProduct,
        'date_expire': this.lotSingle.dateExpire,
        'unit_init': this.lotSingle.unitInit,
        'cost': this.lotSingle.cost,
        'note': this.lotSingle.note,
      }
    },
  },

  watch: {
    warehouses () {
      sidebarModule.init(this.getObject(require('../sidebar.json')))
      sidebarModule.update(this.sidebar, 1, submenuModule.getWarehouses(this.warehouses))
      sidebarModule.update(this.sidebar, 6, [{
        'name': this.query.code,
        'router': {
          'name': 'InventoryLotEdit',
          'query': {
            id: this.query.id,
            name: this.query.code,
          },
        },
        'activeClass': 'active',
      }], {
        sidebarAtIdx: 6,
        submenuAtIdx: 2,
      })
    },
  },

  created () {
    this.fetchData()
  },

  beforeMount () {
    // init page
    this.initPage({
      section: 'app',
      name: this.$t('app.inventory.title'),
      current: {
        app_name: 'inventory',
        app_id: 3,
        module_id: 7,
      },
    })
    // set sidebar stockcontrols ui
    sidebarModule.init(this.getObject(require('../sidebar.json')))
    sidebarModule.update(this.sidebar, 1, submenuModule.getWarehouses(this.warehouses))
    sidebarModule.update(this.sidebar, 6, [{
      'name': this.query.code,
      'router': {
        'name': 'InventoryLotEdit',
        'query': {
          id: this.query.id,
          name: this.query.code,
        },
      },
      'activeClass': 'active',
    }], {
      sidebarAtIdx: 6,
      submenuAtIdx: 2,
    })
  },

  destroyed () {
    this.leavePage(this.$Progress, [])
  },

  methods: {
    ...mapActions('lot', {
      getLotSingle: 'getLotSingle',
      updateLot: 'updateLot',
      updateLotStatus: 'updateLotStatus',
      deleteLot: 'deleteLot',
      createLot: 'createLot',
    }),
    async fetchData () {
      this.loading = true
      await Promise.all([
        this.$store.dispatch('getWarehouses'),
        this.$store.dispatch('getProducts'),
        this.getLotSingle(this.query.id),
      ])
      if (!this.lotSingle.lotId) {
        this.notify(this.$vs, {
          text: this.$t('message.notfounddata'),
          color: 'warning',
          icon: 'warning',
        })
        this.$router.push({ name: 'InventoryLotList' })
      }
      this.loading = false
      this.showFinish(this.$Progress)
    },
    getFormCreate (form = {}) {
      const productInfo = this.getProductInfoBySkuId(form.skuId)
      const productId = productInfo.product_id
      const stockId = productInfo
        .prices.find((price) => price.sku_id === form.skuId)
        .stocks.find((stock) => stock.warehouse_id === form.whId)
        .stock_id
      return {
        'stock_id': stockId,
        'sku_id': form.skuId,
        'product_id': productId,
        'wh_id': form.whId,
        'bin_location_id': form.binId,
        'gr_id': null,
        'po_id': null,
        'date_in': toISODateString(form.dateIn),
        'date_product': toISODateString(form.dateProduct),
        'date_expire': toISODateString(form.dateExpire),
        'unit': form.unit,
        'cost': form.cost,
        'note': form.note,
      }
    },
    async handleSubmit (form = {}) {
      this.isSubmitted = true
      const data = this.getFormCreate(form)
      const responseUpdate = await this.updateLot({ id: this.query.id, data })
      if (responseUpdate.success) {
        this.isSubmitted = false
        this.notify(this.$vs, {
          text: this.$t('message.hasbeenupdated', { msg: this.lotSingle.lotCode }),
          color: 'success',
          icon: 'cloud',
        })
        this.fetchData()
      }
    },
    handleSelectOption (value) {
      if (value === 'delete') {
        this.onOpenModal()
      } else if (value === 'clone') {
        this.openCloneModal()
      }
    },
    async handleSelectStatus (value) {
      this.isSubmitted = true
      this.loadingStatus = true
      const data = { is_ready: value }
      const response = await this.updateLotStatus({ id: this.query.id, data })
      this.loadingStatus = false
      this.isSubmitted = false
      this.fetchData()
    },
    onOpenModal () {
      this.modal.show = true
    },
    handleCloseModal () {
      this.modal.show = false
    },
    handleCancleModal () {
      this.modal.show = false
    },
    async handleSumitModal () {
      this.modal.loading = true
      const response = await this.deleteLot(this.query.id)
      if (response.success) {
        this.notify(this.$vs, {
          text: this.$t('message.hasbeendeleted', { msg: this.lotSingle.lotCode }),
          color: 'danger',
          icon: 'delete',
        })
        this.modal.loading = false
        this.modal.show = false
        this.$router.push({ name: 'InventoryLotList' })
      }
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/apps/inventory/lotnumber/Edit.vue